import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { practitionerService, ExamSubmission, ExamResult } from '../../services/practitionerService';
import { TrainingAssignment, TrainingModule, Exam } from '../../types/training';
import { useContentProtection } from '../../hooks/useContentProtection';

export const ExamTaking: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<TrainingAssignment | null>(null);
  const [module, setModule] = useState<TrainingModule | null>(null);
  const [exam, setExam] = useState<Exam | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ExamResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Enable content protection for exam content
  const { protectionStyles, printProtectionCSS } = useContentProtection({
    disablePrint: true,
    disableRightClick: true,
    disableTextSelection: true,
    disableCopy: true,
    disableScreenshot: true,
  });

  const loadExamData = useCallback(async () => {
    if (!assignmentId) return;

    try {
      setLoading(true);
      setError(null);
      
      const assignmentData = await practitionerService.getAssignment(assignmentId);
      setAssignment(assignmentData);

      const moduleData = await practitionerService.getModule(assignmentData.moduleId);
      setModule(moduleData);

      if (moduleData.examId) {
        const examData = await practitionerService.getExam(moduleData.examId);
        setExam(examData);

        // Start exam attempt
        const attemptResponse = await practitionerService.startExam(assignmentId);
        setAttemptId(attemptResponse.attemptId);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load exam');
      console.error('Error loading exam:', err);
    } finally {
      setLoading(false);
    }
  }, [assignmentId]);

  useEffect(() => {
    loadExamData();
  }, [loadExamData]);

  const handleAnswerChange = (questionId: string, optionId: string) => {
    setAnswers(new Map(answers.set(questionId, optionId)));
  };

  const handleSubmit = async () => {
    if (!attemptId || !exam) return;

    // Check if all questions are answered
    const unansweredCount = exam.questions.length - answers.size;
    if (unansweredCount > 0) {
      if (!window.confirm(`You have ${unansweredCount} unanswered question(s). Submit anyway?`)) {
        return;
      }
    }

    try {
      setSubmitting(true);
      setError(null);

      const submission: ExamSubmission = {
        answers: Array.from(answers.entries()).map(([questionId, selectedOptionId]) => ({
          questionId,
          selectedOptionId,
        })),
      };

      const examResult = await practitionerService.submitExam(attemptId, submission);
      setResult(examResult);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to submit exam');
      console.error('Error submitting exam:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReturnToTraining = () => {
    navigate(`/training/${assignmentId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !exam) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
        <button
          onClick={handleReturnToTraining}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Training
        </button>
      </div>
    );
  }

  if (!assignment || !module || !exam) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Exam not found</p>
        <button
          onClick={handleReturnToTraining}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Training
        </button>
      </div>
    );
  }

  // Show results if exam is submitted
  if (result) {
    const handleViewCertificate = async () => {
      if (result.certificateId) {
        try {
          const response = await practitionerService.getCertificateUrl(result.certificateId);
          window.open(response.url, '_blank');
        } catch (err) {
          console.error('Error getting certificate URL:', err);
          // Fallback to certificates page
          navigate('/certificates');
        }
      } else {
        navigate('/certificates');
      }
    };

    return (
      <div className="max-w-4xl mx-auto px-4 py-6" style={protectionStyles}>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-center mb-6">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              result.passed ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {result.passed ? (
                <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <h2 className={`text-2xl font-bold mb-2 ${result.passed ? 'text-green-600' : 'text-red-600'}`}>
              {result.passed ? 'Congratulations!' : 'Not Passed'}
            </h2>
            <p className="text-3xl font-bold text-gray-900 mb-2">{result.score.toFixed(2)}%</p>
            <p className="text-gray-600">
              Pass rate: {exam.passRate}%
            </p>
            
            {/* Certificate link for passed exams */}
            {result.passed && (
              <div className="mt-4 p-4 bg-green-50 rounded-lg">
                <p className="text-green-800 mb-3">
                  🎉 Your certificate has been generated!
                </p>
                <button
                  onClick={handleViewCertificate}
                  className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Certificate
                </button>
              </div>
            )}
          </div>

          {/* Question Results */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Question Results</h3>
            {!result.passed && (
              <p className="text-sm text-gray-600 mb-4">
                Review the training content and try again. Correct answers are not shown to help you learn.
              </p>
            )}
            <div className="space-y-4">
              {exam.questions.map((question, index) => {
                const answer = result.answers.find(a => a.questionId === question.id);
                const isCorrect = answer?.correct || false;
                const selectedOption = question.options.find(o => o.id === answer?.selectedOptionId);
                const correctOption = question.options.find(o => o.id === question.correctOptionId);

                return (
                  <div key={question.id} className={`border-l-4 pl-4 ${isCorrect ? 'border-green-500' : 'border-red-500'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 mb-2">
                          {index + 1}. {question.questionText}
                        </p>
                        <p className="text-sm text-gray-600">
                          Your answer: <span className={isCorrect ? 'text-green-600' : 'text-red-600'}>
                            {selectedOption?.text || 'Not answered'}
                          </span>
                        </p>
                        {/* Only show correct answer if exam was passed */}
                        {!isCorrect && result.passed && (
                          <p className="text-sm text-gray-600">
                            Correct answer: <span className="text-green-600">{correctOption?.text}</span>
                          </p>
                        )}
                      </div>
                      <div>
                        {isCorrect ? (
                          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <button
              onClick={handleReturnToTraining}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Return to Training
            </button>
          </div>
        </div>

        {/* CSS to disable printing */}
        <style>{printProtectionCSS}</style>
      </div>
    );
  }

  // Show exam questions
  const sortedQuestions = [...exam.questions].sort((a, b) => a.order - b.order);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" style={protectionStyles}>
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{exam.title}</h2>
          <p className="text-sm text-gray-600 mt-1">
            Pass rate: {exam.passRate}% | Questions: {exam.questions.length}
          </p>
          {assignment && (
            <p className="text-sm text-gray-600">
              Attempts remaining: {assignment.maxAttempts - assignment.attempts}
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-6">
          {sortedQuestions.map((question, index) => (
            <div key={question.id} className="border border-gray-200 rounded-lg p-4">
              <p className="font-medium text-gray-900 mb-3">
                {index + 1}. {question.questionText}
              </p>
              <div className="space-y-2">
                {question.options.map((option) => (
                  <label
                    key={option.id}
                    className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="radio"
                      name={`question-${question.id}`}
                      value={option.id}
                      checked={answers.get(question.id) === option.id}
                      onChange={() => handleAnswerChange(question.id, option.id)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-3 text-gray-900">{option.text}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex justify-between items-center">
          <button
            onClick={handleReturnToTraining}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <div className="flex items-center space-x-4">
            <p className="text-sm text-gray-600">
              {answers.size} of {exam.questions.length} answered
            </p>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {submitting ? 'Submitting...' : 'Submit Exam'}
            </button>
          </div>
        </div>
      </div>

      {/* CSS to disable printing */}
      <style>{printProtectionCSS}</style>
    </div>
  );
};
